<template>
    <div>
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless hover class="list">
            <b-thead>
                <b-tr>
                    <b-th>Invoice:</b-th>
                    <b-th>Client:</b-th>
                    <b-th>Items:</b-th>
                    <b-th>Total:</b-th>
                    <b-th>Balance:</b-th>
                    <b-th>Outstanding:</b-th>
                    <b-th>Created:</b-th>
                    <b-th>Updated:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="invoice in invoices" :key="invoice.id">
                    <b-td class="text-nowrap">
                        <invoice-link
                            v-if="invoice.encoded_id"
                            :invoice="invoice"
                            :newTab="false"
                        />
                        <i
                            v-if="
                                invoice.status == BILLINGS.INVOICE.TYPE_LOCKED
                            "
                            class="cus-page-white-key"
                        ></i>
                    </b-td>
                    <b-td>
                        <client-name
                            v-if="invoice.client"
                            :client="invoice.client"
                        />
                        <span v-else>-</span>
                    </b-td>
                    <b-td>
                        {{ invoice.totalItems }}
                    </b-td>

                    <b-td>
                        {{ _toCurrency(invoice.amount) }}
                    </b-td>
                    <b-td>
                        {{ _toCurrency(invoice.balance) }}
                    </b-td>
                    <b-td>
                        {{ invoice.outstanding_days }}
                    </b-td>
                    <b-td class="text-nowrap">
                        {{ _carbonDateDisplay(invoice.create_at) }}
                    </b-td>
                    <b-td class="text-nowrap">
                        {{ _carbonDateDisplay(invoice.update_at) }}
                    </b-td>
                    <b-td>
                        <spinner v-if="invoice.isLoading" />
                        <b-dropdown
                            v-else-if="
                                invoice.status != 6 &&
                                (invoice.isCanPayInvoice ||
                                    _hasAccess('billing.invoice.email') ||
                                    _hasAccess('billing.invoice.print') ||
                                    invoice.isCanSendSMS ||
                                    invoice.isCanCreateWriteOff ||
                                    invoice.isCanMarkAsPaid ||
                                    invoice.isCanDelete)
                            "
                            variant="link"
                            toggle-class="text-decoration-none"
                            no-caret
                            right
                        >
                            <template v-slot:button-content>
                                <i class="cus-cog-down"></i>
                            </template>

                            <b-dropdown-item
                                href="#"
                                v-if="invoice.isCanPayInvoice"
                                @click="createPayment(invoice)"
                            >
                                <i class="cus-money"></i>
                                New Payment
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="_hasAccess('billing.invoice.email')"
                                href="#"
                                @click="openInvoiceEmailModal(invoice)"
                            >
                                <i class="cus-email"></i>
                                Email Invoice
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="_hasAccess('billing.invoice.print')"
                                href="#"
                                @click="printPDF(invoice)"
                            >
                                <i class="cus-printer"></i>
                                Print Invoice
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="invoice.isCanSendSMS"
                                href="#"
                                @click="openInvoiceSMSModal(invoice)"
                            >
                                <i class="cus-phone"></i>
                                Text (SMS) Invoice
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="invoice.isCanCreateWriteOff"
                                href="#"
                                @click="openInvoiceWriteOffModal(invoice)"
                            >
                                <i class="cus-page-white-put"></i>
                                Write Off
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="invoice.isCanMarkAsPaid"
                                href="#"
                                @click="markAsPaid(invoice)"
                            >
                                <i class="cus-accept"></i>
                                Mark as Paid
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="invoice.isCanDelete"
                                href="#"
                                @click="openInvoiceDeleteModal(invoice)"
                            >
                                <i class="cus-delete"></i>
                                Delete Invoice
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-td>
                </b-tr>
                <b-tr v-if="invoices.length === 0">
                    <b-td colspan="8">
                        <i>No records available</i>
                    </b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="meta && meta.lastPage > 1 && !isLoading"
            class="vet-pagination"
            align="right"
            v-model="meta.currentPage"
            :total-rows="meta.total"
            :per-page="meta.perPage"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="getInvoices"
        />

        <invoice-email-modal
            modal-ref="invoice-email-modal"
            ref="invoice-email-modal"
            :invoice="selectedInvoice"
            :client="clientDetails"
        />
        <modal-invoice-send-SMS ref="modal-invoice-send-sms" />

        <invoice-write-off
            modal-ref="invoice-write-off"
            ref="invoice-write-off"
            :invoice="selectedInvoice"
            @reload="getInvoices(meta.currentPage)"
        />
        <invoice-delete-modal
            modal-ref="invoice-delete-modal"
            ref="invoice-delete-modal"
            :invoice="selectedInvoice"
            @reload="getInvoices(meta.currentPage)"
        />
        <createPayment
            ref="createPaymentWindow"
            :invoice="selectedInvoice"
            :client="selectedClient"
            @reload="redirectOtherTab('tab-payments')"
        />
        <exportPrintPdf
            ref="exportPrintPdf"
            v-if="selectedInvoice"
            :invoice="selectedInvoice"
            @show-loading="
                isLoading => {
                    selectedInvoice.isLoading = isLoading;
                }
            "
        />
    </div>
</template>

<script>
/**
 * @name InvoiceList
 * @description List of invoices in billing module
 * @see /apps/assets/js/module/billing/invoice.js
 * @see /apps/application/views/staff/billing/invoice/invoice/list.php
 * @see /apps/application/views/staff/billing/sub/navbar.php
 */
import Loader from "@/components/elements/loader-list";
import BillingInvoiceServices from "@/services/billing/billing-invoice";
import EXTENSIONS from "@/constants/extensions";
import BILLINGS from "@/constants/billings";

const INVOICE_LIST_STATUS_ALL = 3;
export default {
    mixins: {},
    components: {
        Loader,
        Spinner: () => import("@/components/elements/spinner"),
        ClientName: () => import("@/components/commons/client-name"),
        InvoiceLink: () => import("@/components/commons/invoice-link"),
        InvoiceEmailModal: () =>
            import("@/components/client-invoices/invoice-email-modal"),
        ModalInvoiceSendSMS: () =>
            import("@/components/invoice-sms/modal-invoice-send-sms"),
        InvoiceWriteOff: () =>
            import("@/components/client-invoices/invoice-write-off"),
        InvoiceDeleteModal: () =>
            import("@/components/client-invoices/invoice-delete-modal"),
        CreatePayment: () =>
            import("@/components/new-payment/payment-modal.vue"),
        ExportPrintPdf: () =>
            import("@/components/billing-invoice/export-print-pdf.vue")
    },
    props: {
        status: {
            type: Number,
            default: INVOICE_LIST_STATUS_ALL
        }
    },

    data() {
        return {
            EXTENSIONS,
            BILLINGS,
            isLoading: false,
            invoices: [],
            selectedInvoice: {},
            selectedClient: null,
            meta: null
        };
    },
    computed: {
        clientDetails() {
            return this.$store.getters["client/clientDetails"];
        }
    },
    async mounted() {
        this.getInvoices();
    },
    methods: {
        canDelete(invoice) {
            return (
                this._hasAccess("billing.invoice.delete") &&
                !this.isPaid(invoice)
            );
        },
        canMarkAsPaid(invoice) {
            return (
                this._hasAccess("billing.invoice.mark.paid") &&
                !this.isPaid(invoice) &&
                Number(invoice.total_amount) == 0 &&
                invoice.totalItems > 0 &&
                this.isBusinessActorActive(invoice)
            );
        },
        canCreateWriteOff(invoice) {
            return (
                this._hasAccess("billing.writeoff.create") &&
                !this.isPaid(invoice) &&
                invoice.balance > 0 &&
                this.isBusinessActorActive(invoice)
            );
        },
        canPayInvoice(invoice) {
            return (
                this._hasAccess("billing.payment.create") &&
                !this.isPaid(invoice) &&
                invoice.amount > 0 &&
                invoice.balance > 0 &&
                this.isBusinessActorActive(invoice)
            );
        },
        canSendSMS() {
            return (
                (this._isExtensionEnabled(EXTENSIONS.CODE.CODE_SMS) ||
                    this._isExtensionEnabled(
                        EXTENSIONS.CODE.CODE_2_WAY_MESSAGING
                    )) &&
                this._hasAccess("billing.invoice.sms")
            );
        },
        isBusinessActorActive(invoice) {
            return (
                invoice.client &&
                invoice.client.single_business_actor &&
                invoice.client.single_business_actor.isactive
            );
        },
        isPaid(invoice) {
            return (
                (invoice.status == BILLINGS.INVOICE.TYPE_PAID &&
                    invoice.balance == 0) ||
                invoice.status == BILLINGS.INVOICE.TYPE_WRITEOFF ||
                invoice.status == BILLINGS.INVOICE.TYPE_CLOSED
            );
        },
        getInvoices(page = 1) {
            let self = this;
            self.invoices = [];
            self.isLoading = true;
            const params = {
                page: page,
                perPage: self.PER_PAGE,
                status: self.status
            };
            BillingInvoiceServices.getInvoices(params)
                .then(res => {
                    if (res) {
                        self.invoices = res.resource.data.map(inv => {
                            inv.isLoading = false;
                            inv.totalItems =
                                inv.active_invoice_items &&
                                inv.active_invoice_items.length
                                    ? inv.active_invoice_items.length
                                    : 0;
                            inv.isCanPayInvoice = this.canPayInvoice(inv);
                            inv.isCanSendSMS = this.canSendSMS();
                            inv.isCanCreateWriteOff = this.canCreateWriteOff(
                                inv
                            );
                            inv.isCanMarkAsPaid = this.canMarkAsPaid(inv);
                            inv.isCanDelete = this.canDelete(inv);
                            return inv;
                        });
                        self.meta = res.meta;
                    } else {
                        throw "Error : Unable to retrieve invoices list";
                    }
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to retrieve invoices list",
                        "danger"
                    );
                    console.error(error);
                })
                .finally(() => {
                    self.isLoading = false;
                });
        },
        async printPDF(invoice) {
            let self = this;
            self.selectedInvoice = invoice;
            await this.$nextTick();
            self.$refs.exportPrintPdf.init();
        },
        markAsPaid(invoice) {
            let self = this;
            invoice.isLoading = true;
            BillingInvoiceServices.markAsPaid(invoice.id)
                .then(response => {
                    if (response && response.code === 200) {
                        self._message(response.message);
                        self.getInvoices(self.meta.currentPage);
                    } else if (response && response.code == 400) {
                        throw response.message;
                    } else {
                        throw "Error : Unable mark as paid";
                    }
                })
                .catch(error => {
                    self._message(error, "danger");
                    console.error(error);
                })
                .finally(() => {
                    invoice.isLoading = false;
                });
        },
        async openInvoiceEmailModal(invoice) {
            this.selectedInvoice = invoice;
            this.selectedInvoice.isLoading = true;
            await this.$store.dispatch("client/getClientDetails", {
                id: this.selectedInvoice.client_id
            });
            this.selectedInvoice.isLoading = false;
            this.$refs["invoice-email-modal"].open();
        },
        openInvoiceSMSModal(invoice) {
            this.selectedInvoice = invoice;
            this.$refs["modal-invoice-send-sms"].open(this.selectedInvoice.id);
        },
        openInvoiceWriteOffModal(invoice) {
            this.selectedInvoice = invoice;
            this.$refs["invoice-write-off"].open();
        },
        async openInvoiceDeleteModal(invoice) {
            this.selectedInvoice = invoice;
            if (invoice.totalItems > 0) {
                let date = this._fromDateCarbon(invoice.start).toFormat("X");

                this.$store.dispatch("checkBillingLock", {
                    date,
                    onProceed: () => this.$refs["invoice-delete-modal"].open(),
                    onError: message => this._message(message, "danger")
                });
            } else {
                this.$refs["invoice-delete-modal"].open();
            }
        },
        createPayment(invoice) {
            this.selectedInvoice = invoice;
            this.selectedClient = invoice.client;
            this.$refs.createPaymentWindow.open();
        },
        redirectOtherTab(tab) {
            this.$emit("redirect-other-tab", tab);
        }
    }
};
</script>
